import { NavPage } from '@/components/breadcrumbs/Breadcrumbs';
import { CataloguePageHeader } from '@/components/catalogue/Header';
import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { useModal } from '@/components/modal/useModal';
import SiChevronLeft from '@/icons/SiChevronLeft';
import { useCurrentPurchaseOrderLink } from '@/lib/getMyLinks';
import { PurchaseOrderDeliveryOption } from '@/services/api/contracts/catalog/bundles';
import { useMspDetailsForCustomer } from '@/services/api/hooks/companies';
import { RoleType, useRole } from '@/services/authentication/hooks/user';
import { usePurchaseOrder } from '@/services/purchaseOrders/usePurchaseOrders';
import { dateStringToDisplayDateAndTime } from '@/utils/date';
import { getDescriptionFromRichTextArea, isRichTextAreaEmpty } from '@/utils/richText';
import { FunctionComponent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { PurchaseOrderStatusChit } from '../PurchaseOrderStatusChit';
import PurchaseOrderTotalCostSummary from '../PurchaseOrderTotalCostSummary';
import { usePurchaseOrderDetails } from '../usePurchaseOrderDetails';
import { usePurchaseOrderMainDeviceCount } from '../usePurchaseOrderMainDeviceCount';
import PurchaseOrderCompleteProductCard from './PurchaseOrderCompleteProductCard';
import UpdateStatusModal from './UpdateStatusModal';

const PurchaseOrderCompletePage: FunctionComponent = () => {
  const navigate = useNavigate();

  const poLink = useCurrentPurchaseOrderLink();

  const { orderId } = useParams();

  if (!orderId) throw new Error('Missing order id');

  const order = usePurchaseOrder(orderId);

  if (!order) throw new Error(`Missing order with id: ${orderId}`);

  const breadcrumbs: NavPage[] = [
    {
      name: 'Orders / Quotes',
      to: poLink
    },
    {
      name: `Order - ${order.name}`,
      to: ``
    }
  ];

  const { products, status, deviceFeeItem } = usePurchaseOrderDetails(order);

  const mainDeviceCount = usePurchaseOrderMainDeviceCount(order);

  const modalProps = useModal<{ purchaseOrderId: string }>();

  const role = useRole();

  const { partnerId, companyId } = useParams();
  if (!(partnerId && companyId)) throw new Error('Missing params');
  const [mspDetails] = useMspDetailsForCustomer(partnerId, companyId);

  const showNotes = Boolean(order.notes && !isRichTextAreaEmpty(order.notes.value));

  return (
    <div className="flex h-full flex-1 flex-col bg-white bg-opacity-50 overflow-y-auto">
      <CataloguePageHeader title={`Order - ${order.name}`} breadcrumbs={breadcrumbs}>
        <div className="flex gap-4">
          <Button onClick={() => navigate(poLink)} variant={ButtonVariant.Mono}>
            <SiChevronLeft className="text-xl" />
            <span>Back to Orders / Quotes</span>
          </Button>
          {role.type === RoleType.Admin && (
            <Button onClick={() => modalProps.open({ purchaseOrderId: order.id! })} variant={ButtonVariant.Primary}>
              <span>Update Status</span>
            </Button>
          )}
        </div>
      </CataloguePageHeader>
      <div className="px-6 py-8 space-y-5 overflow-y-auto">
        <div className="space-y-4">
          <div>
            <p className="text-3xl font-semibold">Order Details</p>
            <PurchaseOrderStatusChit status={status} />
          </div>

          <PurchaseOrderTotalCostSummary />

          <div>
            <p>Name: {order.name}</p>
            {order.createdOn && <p>Date Created: {dateStringToDisplayDateAndTime(order.createdOn)}</p>}
            {order.updatedOn && <p>Last Updated On: {dateStringToDisplayDateAndTime(order.updatedOn)}</p>}
            {order.poReference && <p>PO Reference: {order.poReference}</p>}
            {order.stockingOrderNumber && <p>Stocking Order Number: {order.stockingOrderNumber}</p>}
            {order.partnerReference && <p>Partner Reference: {order.partnerReference}</p>}
            {showNotes && <p>Notes / Special Instructions: {getDescriptionFromRichTextArea(order.notes.value)}</p>}
          </div>

          <div>
            <p>Delivery Details:</p>
            {order.deliveryOption === PurchaseOrderDeliveryOption.Msp && <p>{`${mspDetails.name} Stock`}</p>}
            {order.deliveryOption === PurchaseOrderDeliveryOption.Daas && <p>{`DaaS Stock`}</p>}
            {order.address && (
              <>
                {order.address.name && <p>{order.address.name}</p>}
                {order.address.line1 && <p>{order.address.line1}</p>}
                {order.address.line2 && <p>{order.address.line2}</p>}
                {order.address.city && <p>{order.address.city}</p>}
                {order.address.state && <p>{order.address.state}</p>}
                {order.address.country && <p>{order.address.country}</p>}
                {order.address.postcode && <p>{order.address.postcode}</p>}
              </>
            )}

            {order?.deliveryOption === PurchaseOrderDeliveryOption.Custom && order.contactDetail !== null && (
              <div>
                <p className="font-semibold">Contact:</p>
                <p>Work email: {order.contactDetail?.workEmail}</p>
                {order.contactDetail?.personalEmail && <p>Personal email: {order.contactDetail?.personalEmail}</p>}
                <p>Telephone number: {order.contactDetail?.telephoneNumber}</p>
              </div>
            )}
          </div>

          <p className="text-3xl font-semibold">Products</p>
          <div className="grid h-fit grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4">
            {products.map(product => (
              <PurchaseOrderCompleteProductCard
                product={product}
                quantity={order.bundledItems.find(b => b.catalogItem === product.id)?.count || 1}
                key={product.id}
                showDeviceFee={product.category.isMainDevice}
              />
            ))}
            {deviceFeeItem && <PurchaseOrderCompleteProductCard product={deviceFeeItem} quantity={mainDeviceCount} />}
          </div>
        </div>
      </div>
      <UpdateStatusModal {...modalProps} />
    </div>
  );
};

export default PurchaseOrderCompletePage;
